/**
 * renderSync.ts — the ONE module that owns entity → Object3D (AGENTS.md
 * client render-sync is one module).
 *
 * It buffers snapshots, spawns and removes objects as players appear and vanish,
 * interpolates between the two snapshots bracketing the render time, drives the
 * animation state, and disposes every geometry, material and texture it created.
 * Nothing else in the client may touch the scene graph for players.
 *
 * OTHER players are interpolated, INTERP_DELAY_MS behind the server, and never
 * extrapolated: guessing at the position you are timing a parry against would be
 * worse than seeing it late.
 *
 * The LOCAL player is different. If a `selfView` is supplied it is drawn from that
 * instead — the predicted state from `predict.ts` (AGENTS.md: the local player is predicted) — so
 * your own movement, guard and swing happen on the frame you asked for them. It
 * still comes through here rather than around the side, because this module owning
 * every Object3D is what keeps meshes from leaking.
 */

import * as THREE from 'three';
import {
  createFigure,
  disposeFigure,
  poseFigure,
  setFigureHitbox,
  setFigureWireframe,
  wearKit,
  type Figure,
  type FigurePose,
} from './figure.js';
import type { PredictedView } from './predict.js';
import { setNearDetailVisible } from './surfaces.js';
import {
  FEEL,
  clipDurationMs,
  tickMs,
  unpackLoadout,
  type AnimStateId,
  type PlayerSnapshot,
  type Snapshot,
} from '@dueler/shared';

/** Beyond this distance (m), near-only detail (rivets, fingers, fluting) hides. */
const NEAR_DETAIL_M = 9;

/**
 * Playout clock tuning. The clock runs in SIMULATION time (fractional server
 * ticks) and is advanced by REAL time, which is what keeps interpolation smooth:
 * sampling by arrival time instead puts every millisecond of network jitter
 * straight into the picture, and the result is the stepping you can see.
 */
/** Rate correction per tick of lag error. Gentle: this is a clock, not a spring. */
const PLAYOUT_GAIN = 0.08;
/** Hardest the clock may run fast or slow, as a fraction of real time. */
const PLAYOUT_MAX_RATE = 0.15;
/**
 * Lag error, in ticks, past which the clock re-anchors instead of slewing. A real
 * hitch is better paid off with one jump than with seconds of visibly wrong speed.
 */
const PLAYOUT_RESYNC_TICKS = 12;

interface PlayerView {
  /**
   * The figure: every mesh, every mount, and the pose logic that drives them.
   * This module owns its lifetime and knows nothing else about its insides.
   */
  figure: Figure;
  anim: AnimStateId;
  /** Whether the swing being drawn has already been announced. One swing, one sound. */
  swingLatched: boolean;
  /** Whether the guard was up last frame, so raising and dropping it are events. */
  blocking: boolean;
  /**
   * The packed loadout last applied, compared against the snapshot's so a change
   * of kit is spotted without unpacking and deep-comparing every tick.
   */
  kit: string;
}

/** Keep enough history to interpolate through a hiccup, and no more. */
const MAX_BUFFER = 20;

export interface RenderSync {
  /** Feeds a decoded snapshot in. Out-of-order snapshots are dropped. */
  ingest(snapshot: Snapshot): void;
  /** Advances interpolation to `now` (ms, performance.now clock). */
  update(now: number): void;
  /** Interpolated position of a slot, or undefined if it has no view. */
  positionOf(slot: number): THREE.Vector3 | undefined;
  /** Interpolated facing of a slot, or undefined if it has no view. */
  yawOf(slot: number): number | undefined;
  setWireframe(on: boolean): void;
  /**
   * Draws every player's hurtbox capsule. Debug only.
   *
   * Rides with the arena collider overlay rather than getting a key of its own:
   * "show me the shapes the simulation actually uses" is one question, and
   * answering half of it is how you end up trusting the half you can see.
   */
  setHitboxes(on: boolean): void;
  /** Hides the local player's body in first person, keeping the arms and blade. */
  setSelfBodyVisible(visible: boolean): void;
  /** Newest snapshot seen, for the debug readout. */
  readonly latest: Snapshot | undefined;
  readonly interpolating: boolean;
  dispose(): void;
}

/**
 * How the local player should be drawn this frame, overriding the interpolated
 * snapshot. Returning undefined falls back to interpolation, which is what
 * happens before the first snapshot and while dead.
 */
export type SelfView = (now: number) => PredictedView | undefined;

/**
 * Something visible happened. Reported from what is actually being DRAWN, which is
 * the point: the local player's pose comes from prediction, so their swing is
 * announced without waiting for a round trip, and an opponent's is announced when
 * it appears on screen. Sound hangs off this (`audio.ts`).
 *
 * `swing` fires when the blade goes ACTIVE — when it actually starts cutting — not
 * when the clip begins. The clip begins on the windup, and the windup is not a
 * swing: it can be held at the top for as long as the button is down, so a sound
 * fired there arrives seconds before the blade moves. It also means a feint is
 * silent, which is correct, because a feint never cuts anything.
 *
 * Contacts are NOT in here. Whether steel actually met anything is the server's
 * to say, and it says it in the combat events.
 */
export type ViewEvent =
  | { t: 'swing'; slot: number; clip: 'slash' | 'stab'; pos: THREE.Vector3 }
  | { t: 'guard'; slot: number; up: boolean; pos: THREE.Vector3 };

export function createRenderSync(
  scene: THREE.Scene,
  selfSlot: () => number,
  selfView?: SelfView,
  onViewEvent?: (event: ViewEvent) => void,
  /** Camera for distance LOD. Optional so unit tests can omit it. */
  camera?: THREE.Camera,
): RenderSync {
  const buffer: Snapshot[] = [];
  const views = new Map<number, PlayerView>();
  let lastSeq = -1;
  const camPos = new THREE.Vector3();
  /**
   * Render-clock time of the last update, for the playout clock and the gait.
   * Undefined, not 0, for the same reason as `playTick`: zero is a real timestamp.
   */
  let lastUpdateAt: number | undefined;
  /**
   * Fractional server tick the view is currently showing; undefined until the
   * first snapshot anchors it.
   *
   * Undefined rather than a negative sentinel: a room's first ticks are small
   * numbers, so `tick - delayTicks` is legitimately negative for the first few
   * frames of a match and a "< 0 means unset" test would re-anchor the clock on
   * every one of them.
   */
  let playTick: number | undefined;
  let wireframe = false;
  let hitboxes = false;
  let interpolating = false;
  let selfBodyVisible = true;

  function makeView(slot: number): PlayerView {
    const isSelf = slot === selfSlot();
    const figure = createFigure(isSelf);
    figure.body.visible = !isSelf || selfBodyVisible;
    scene.add(figure.root);
    if (wireframe) setFigureWireframe(figure, true);
    if (hitboxes) setFigureHitbox(figure, true);
    return {
      figure,
      anim: 'idle',
      swingLatched: false,
      blocking: false,
      // Deliberately not the default loadout: whatever the first snapshot says
      // must look like a change, or a player already in plate when you join
      // spawns naked and stays that way.
      kit: '',
    };
  }

  function removeView(slot: number): void {
    const view = views.get(slot);
    if (!view) return;
    disposeFigure(view.figure);
    views.delete(slot);
  }

  /**
   * Finds the two snapshots bracketing a fractional simulation `tick`, or the
   * nearest end if the clock has run outside the buffer.
   *
   * Bracketing by TICK, not by arrival time. Ticks are uniformly spaced by
   * construction — the server emits one per fixed timestep — so samples come out
   * evenly however unevenly the packets turned up. It also interpolates correctly
   * straight across a dropped snapshot, because the gap is known rather than
   * guessed from a longer-than-usual silence.
   */
  function bracket(tick: number): { a: Snapshot; b: Snapshot; alpha: number } | undefined {
    if (buffer.length === 0) return undefined;
    for (let i = buffer.length - 1; i > 0; i -= 1) {
      const b = buffer[i]!;
      const a = buffer[i - 1]!;
      if (a.tick <= tick && tick <= b.tick) {
        const span = b.tick - a.tick;
        return { a, b, alpha: span > 0 ? (tick - a.tick) / span : 1 };
      }
    }
    // Outside the buffer: hold the nearest end rather than extrapolate into a
    // guess the server never authorized.
    const first = buffer[0]!;
    const last = buffer[buffer.length - 1]!;
    return tick < first.tick ? { a: first, b: first, alpha: 0 } : { a: last, b: last, alpha: 1 };
  }

  return {
    ingest(snapshot) {
      // Latest-wins: a snapshot that arrives late is worthless, never authoritative.
      if (snapshot.seq <= lastSeq) return;
      lastSeq = snapshot.seq;
      buffer.push(snapshot);
      while (buffer.length > MAX_BUFFER) buffer.shift();
    },

    update(now) {
      const newest = buffer[buffer.length - 1];
      if (newest === undefined) return;

      // Frame delta on the render clock, so everything time-based here — the
      // playout clock and the gait — freezes together during hitstop. No gameplay
      // is scaled by it (AGENTS.md: fixed timestep).
      const elapsed = lastUpdateAt === undefined ? 0 : Math.max(0, now - lastUpdateAt);
      lastUpdateAt = now;

      // Advance the playout clock: simulation time moving at real time, nudged
      // toward sitting INTERP_DELAY_MS behind the newest snapshot. The nudge is a
      // RATE change, never a jump, so a late or early packet changes how fast the
      // view is catching up rather than where it is.
      const step = tickMs();
      const target = newest.tick - FEEL.INTERP_DELAY_MS / step;
      if (playTick === undefined || Math.abs(target - playTick) > PLAYOUT_RESYNC_TICKS) {
        playTick = target;
      } else {
        const error = target - playTick;
        const rate = Math.min(
          1 + PLAYOUT_MAX_RATE,
          Math.max(1 - PLAYOUT_MAX_RATE, 1 + error * PLAYOUT_GAIN),
        );
        playTick += (elapsed / step) * rate;
      }

      const found = bracket(playTick);
      if (!found) return;
      const { a, b, alpha } = found;
      interpolating = a !== b;

      const dt = elapsed / 1000;
      const seen = new Set<number>();
      for (const target of b.players) {
        seen.add(target.id);
        const view = views.get(target.id) ?? makeView(target.id);
        views.set(target.id, view);

        // Your own player is drawn predicted, everyone else interpolated. The
        // predicted state is already at the current instant, so it is used as-is
        // rather than bracketed: interpolating it would put back the latency it
        // exists to remove.
        const predicted = target.id === selfSlot() ? selfView?.(now) : undefined;
        const pose: FigurePose = predicted ?? target;

        if (predicted !== undefined) {
          view.figure.root.position.set(predicted.pos.x, predicted.pos.y, predicted.pos.z);
          view.figure.root.rotation.y = predicted.yaw;
        } else {
          const from = a.players.find((p) => p.id === target.id) ?? target;
          // Snapshot position is the capsule centre, and so is the group origin.
          view.figure.root.position.set(
            THREE.MathUtils.lerp(from.x, target.x, alpha),
            THREE.MathUtils.lerp(from.y, target.y, alpha),
            THREE.MathUtils.lerp(from.z, target.z, alpha),
          );
          // Yaw is an angle: lerping it directly would spin the long way at the
          // wrap point.
          view.figure.root.rotation.y = interpolateAngle(from.yaw, target.yaw, alpha);
        }

        // Transitions, before the previous pose is overwritten.
        if (onViewEvent !== undefined) {
          const swing = nextSwingLatch(view.swingLatched, pose.anim);
          view.swingLatched = swing.latched;
          if (swing.fire) {
            onViewEvent({
              t: 'swing',
              slot: target.id,
              // Only attack clips latch, so this is one of them.
              clip: pose.anim.id as 'slash' | 'stab',
              pos: view.figure.root.position,
            });
          }
          if (pose.blocking !== view.blocking) {
            onViewEvent({
              t: 'guard',
              slot: target.id,
              up: pose.blocking,
              pos: view.figure.root.position,
            });
          }
        }

        view.anim = pose.anim.id;
        view.blocking = pose.blocking;
        // Kit is server state like anything else in the snapshot, so a change
        // reaches every client the same way a position does. Compared as the
        // packed string it arrived as: cheap, and it cannot disagree with what
        // was actually applied.
        const kit = String(target.kit);
        if (kit !== view.kit) {
          view.kit = kit;
          wearKit(view.figure, unpackLoadout(target.kit), target.id === selfSlot());
        }
        poseFigure(view.figure, pose);

        // Distance LOD: drop near-only mesh detail for far opponents so eight
        // players stay readable without paying full rivet/finger cost.
        if (camera !== undefined) {
          camera.getWorldPosition(camPos);
          const dist = camPos.distanceTo(view.figure.root.position);
          setNearDetailVisible(view.figure.root, dist < NEAR_DETAIL_M);
        }
      }

      // Anyone absent from the newest snapshot has left: no ghosts.
      for (const slot of [...views.keys()]) {
        if (!seen.has(slot)) removeView(slot);
      }
    },

    positionOf(slot) {
      const view = views.get(slot);
      return view ? view.figure.root.position.clone() : undefined;
    },

    yawOf(slot) {
      return views.get(slot)?.figure.root.rotation.y;
    },

    setSelfBodyVisible(visible) {
      selfBodyVisible = visible;
      const view = views.get(selfSlot());
      if (view !== undefined) view.figure.body.visible = visible;
    },

    setWireframe(on) {
      wireframe = on;
      for (const view of views.values()) setFigureWireframe(view.figure, on);
    },

    setHitboxes(on) {
      hitboxes = on;
      for (const view of views.values()) setFigureHitbox(view.figure, on);
    },

    get latest() {
      return buffer[buffer.length - 1];
    },

    get interpolating() {
      return interpolating;
    },

    dispose() {
      for (const slot of [...views.keys()]) removeView(slot);
      buffer.length = 0;
    },
  };
}

/**
 * How far back down the windup the clip clock must fall before a swing can be
 * announced again, as a fraction of the windup.
 *
 * This is hysteresis, and it is load-bearing. The LOCAL player's clip time is
 * predicted, and prediction rebuilds the swing from the server's clip time plus a
 * replay of however many commands are unacked THIS frame — so it jitters backwards
 * by up to a tick as snapshots land. Re-arming on any backwards step therefore
 * fires a second sound whenever that jitter straddles the active boundary, and
 * because each copy is panned wherever the camera happened to be pointing, the
 * duplicates smear across the stereo field as the player turns. Requiring the
 * clock to fall back to the middle of the windup means only a genuinely new swing
 * can re-arm it.
 */
export const SWING_REARM_FRACTION = 0.5;

/**
 * One swing, one sound: given whether this view's current swing has already been
 * announced, decides whether to announce it now.
 *
 * The moment worth announcing is the clip clock reaching the ACTIVE window — when
 * the sword actually starts moving through space. The clip *starting* is not: that
 * is the windup, which parks at its top for as long as the button is held, so a
 * sound fired there arrives however many seconds early. A feint never reaches the
 * boundary at all and stays silent, which is right, because nothing was cut.
 *
 * The boundary comes from the same `FEEL.CLIPS` windows the server sweeps with, so
 * the sound and the hitbox agree by construction.
 */
export function nextSwingLatch(
  latched: boolean,
  anim: { id: AnimStateId; t: number },
): { latched: boolean; fire: boolean } {
  if (anim.id !== 'slash' && anim.id !== 'stab') return { latched: false, fire: false };

  const activeAt = FEEL.CLIPS[anim.id].windupMs / clipDurationMs(anim.id);
  // Back near the start of a clip: a new swing is winding up, so arm the latch.
  if (anim.t < activeAt * SWING_REARM_FRACTION) return { latched: false, fire: false };
  // Cutting, and not yet announced.
  if (anim.t >= activeAt && !latched) return { latched: true, fire: true };
  // Anywhere in between — including the whole active window and the recovery —
  // hold whatever state we had. This is what makes prediction jitter silent.
  return { latched, fire: false };
}

/** Shortest-path angle interpolation. */
export function interpolateAngle(from: number, to: number, alpha: number): number {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return from + delta * alpha;
}
